import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workersAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Pencil, Trash2, Plus, Wrench, Crown, Truck,
  ChevronLeft, ChevronRight, Clock, Calendar, User, X, Phone, IndianRupee
} from 'lucide-react';

const ROLE_CONFIG = {
  karigar: { label: 'Karigar', icon: Wrench, color: 'var(--brand-navy)', bg: 'rgba(27,42,74,0.08)' },
  master: { label: 'Master', icon: Crown, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
  outsourced_vendor: { label: 'Outsourced Vendor', icon: Truck, color: '#0E7490', bg: 'rgba(14,116,144,0.08)' },
};

const GARMENT_LABELS = {
  jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta', pyjama: 'Pyjama',
  pattern_draft: 'Pattern Draft', pattern_cut: 'Pattern Cut',
};

const GARMENT_TYPES = Object.keys(GARMENT_LABELS);

const AVAIL_STATUS = {
  available: { label: 'Available', color: '#059669', bg: 'rgba(5,150,105,0.12)' },
  half_day: { label: 'Half Day', color: '#D97706', bg: 'rgba(217,119,6,0.12)' },
  leave: { label: 'Leave', color: '#DC2626', bg: 'rgba(220,38,38,0.12)' },
  holiday: { label: 'Holiday', color: '#6366F1', bg: 'rgba(99,102,241,0.12)' },
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function WorkerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = id === 'new';
  const canEdit = ['Admin', 'Production'].includes(user?.role);

  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [editing, setEditing] = useState(isNew);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '', role: 'karigar', phone: '', daily_rate: '',
    overtime_rate_per_hour: '',
    shift_hours: '10', is_active: true, is_temporary: false,
    temp_start_date: '', temp_end_date: '', notes: '',
    piece_rate_per_garment: {},
  });

  // Capability dialog
  const [capDialogOpen, setCapDialogOpen] = useState(false);
  const [capForm, setCapForm] = useState({ garment_type: '', output_per_day: '', hours_per_unit: '' });
  const [editingCapId, setEditingCapId] = useState(null);

  // Availability calendar
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
  const [availability, setAvailability] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);

  const fetchWorker = useCallback(async () => {
    if (isNew) return;
    try {
      setLoading(true);
      const res = await workersAPI.get(id);
      setWorker(res.data);
      setForm({
        name: res.data.name,
        role: res.data.role,
        phone: res.data.phone || '',
        daily_rate: res.data.daily_rate != null ? String(res.data.daily_rate) : '',
        overtime_rate_per_hour: res.data.overtime_rate_per_hour != null ? String(res.data.overtime_rate_per_hour) : '',
        shift_hours: String(res.data.shift_hours || 10),
        is_active: res.data.is_active,
        is_temporary: res.data.is_temporary,
        temp_start_date: res.data.temp_start_date || '',
        temp_end_date: res.data.temp_end_date || '',
        notes: res.data.notes || '',
        piece_rate_per_garment: res.data.piece_rate_per_garment || {},
      });
    } catch (err) {
      toast.error('Failed to load worker');
      navigate('/app/production/workers');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, navigate]);

  const fetchAvailability = useCallback(async () => {
    if (isNew || !id) return;
    try {
      setLoadingAvail(true);
      const res = await workersAPI.getAvailability(id, calYear, calMonth);
      setAvailability(res.data);
    } catch (err) {
      console.error('Failed to load availability');
    } finally {
      setLoadingAvail(false);
    }
  }, [id, isNew, calYear, calMonth]);

  useEffect(() => { fetchWorker(); }, [fetchWorker]);
  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        role: form.role,
        phone: form.phone || null,
        daily_rate: form.daily_rate ? parseFloat(form.daily_rate) : null,
        overtime_rate_per_hour: form.overtime_rate_per_hour ? parseFloat(form.overtime_rate_per_hour) : null,
        shift_hours: parseFloat(form.shift_hours) || 10,
        is_active: form.is_active,
        is_temporary: form.is_temporary,
        temp_start_date: form.is_temporary ? (form.temp_start_date || null) : null,
        temp_end_date: form.is_temporary ? (form.temp_end_date || null) : null,
        notes: form.notes || null,
        piece_rate_per_garment: Object.keys(form.piece_rate_per_garment).length > 0
          ? form.piece_rate_per_garment : null,
      };

      if (isNew) {
        const res = await workersAPI.create(payload);
        toast.success('Worker created');
        navigate(`/app/production/workers/${res.data.id}`, { replace: true });
      } else {
        await workersAPI.update(id, payload);
        toast.success('Worker updated');
        setEditing(false);
        fetchWorker();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this worker? This cannot be undone.')) return;
    try {
      await workersAPI.delete(id);
      toast.success('Worker deleted');
      navigate('/app/production/workers');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // ---- Capability handlers ----
  const openCapDialog = (cap = null) => {
    if (cap) {
      setEditingCapId(cap.id);
      setCapForm({
        garment_type: cap.garment_type,
        output_per_day: String(cap.output_per_day),
        hours_per_unit: String(cap.hours_per_unit),
      });
    } else {
      setEditingCapId(null);
      setCapForm({ garment_type: '', output_per_day: '', hours_per_unit: '' });
    }
    setCapDialogOpen(true);
  };

  const saveCap = async () => {
    if (!capForm.garment_type || !capForm.output_per_day || !capForm.hours_per_unit) {
      toast.error('All fields are required'); return;
    }
    try {
      const data = {
        garment_type: capForm.garment_type,
        output_per_day: parseFloat(capForm.output_per_day),
        hours_per_unit: parseFloat(capForm.hours_per_unit),
      };
      if (editingCapId) {
        await workersAPI.updateCapability(editingCapId, data);
        toast.success('Capability updated');
      } else {
        await workersAPI.addCapability(id, data);
        toast.success('Capability added');
      }
      setCapDialogOpen(false);
      fetchWorker();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save capability');
    }
  };

  const deleteCap = async (capId) => {
    try {
      await workersAPI.deleteCapability(capId);
      toast.success('Capability removed');
      fetchWorker();
    } catch (err) {
      toast.error('Failed to remove capability');
    }
  };

  // ---- Availability handlers ----
  const setDayAvailability = async (dateStr, status) => {
    try {
      await workersAPI.setAvailability(id, { date: dateStr, status, notes: null });
      fetchAvailability();
    } catch (err) {
      toast.error('Failed to update availability');
    }
  };

  const clearDayAvailability = async (dateStr) => {
    try {
      await workersAPI.deleteAvailability(id, dateStr);
      fetchAvailability();
    } catch (err) {
      // May not exist, that's fine
    }
  };

  if (loading) {
    return (<div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 w-full" /><Skeleton className="h-48 w-full" /></div>);
  }

  const roleConfig = ROLE_CONFIG[form.role] || ROLE_CONFIG.karigar;
  const initials = form.name ? form.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'W';

  return (
    <div className="space-y-6" data-testid="worker-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/production/workers')}
            className="h-9 px-2" data-testid="back-to-workers">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-base font-semibold"
              style={{ backgroundColor: roleConfig.bg, color: roleConfig.color }}>
              {initials}
            </div>
            <div>
              <h1 className="text-xl font-semibold" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
                {isNew ? 'New Worker' : form.name}
              </h1>
              {!isNew && (
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className="text-[10px] font-medium px-1.5 py-0 rounded-md"
                    style={{ backgroundColor: roleConfig.bg, color: roleConfig.color, borderColor: 'transparent' }}>
                    {roleConfig.label}
                  </Badge>
                  <div className={`w-2 h-2 rounded-full ${form.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  <span className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>{form.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {!isNew && canEdit && (
          <div className="flex gap-2">
            {!editing ? (
              <Button variant="outline" onClick={() => setEditing(true)} className="h-9 rounded-lg text-xs gap-1.5"
                data-testid="edit-worker-button">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </Button>
            ) : (
              <Button variant="outline" onClick={() => { setEditing(false); fetchWorker(); }} className="h-9 rounded-lg text-xs">
                Cancel
              </Button>
            )}
            <Button variant="ghost" onClick={handleDelete} className="h-9 rounded-lg text-xs" style={{ color: 'var(--danger)' }}
              data-testid="delete-worker-button">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Personal Info Card */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Personal Information & Rates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Name *</Label>
              <Input data-testid="worker-name-input" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))}
                disabled={!editing} className="h-10 rounded-lg text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Role</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({...p, role: v}))} disabled={!editing}>
                <SelectTrigger className="h-10 rounded-lg text-sm" data-testid="worker-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="karigar">Karigar</SelectItem>
                  <SelectItem value="master">Master</SelectItem>
                  <SelectItem value="outsourced_vendor">Outsourced Vendor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Phone</Label>
              <Input data-testid="worker-phone-input" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                disabled={!editing} className="h-10 rounded-lg text-sm" placeholder="+91..." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Daily Rate (INR)</Label>
              <Input data-testid="worker-daily-rate-input" type="number" value={form.daily_rate}
                onChange={e => setForm(p => ({...p, daily_rate: e.target.value}))}
                disabled={!editing} className="h-10 rounded-lg text-sm" placeholder="e.g. 800" />
              {form.daily_rate && form.shift_hours && (
                <p className="text-[10px] tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                  {'\u2248'} {'\u20b9'}{(parseFloat(form.daily_rate) / parseFloat(form.shift_hours)).toFixed(0)}/hr regular
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Overtime Rate (INR/hr)</Label>
              <Input data-testid="worker-overtime-rate-input" type="number" value={form.overtime_rate_per_hour}
                onChange={e => setForm(p => ({...p, overtime_rate_per_hour: e.target.value}))}
                disabled={!editing} className="h-10 rounded-lg text-sm" placeholder="e.g. 120" />
              {form.overtime_rate_per_hour && form.daily_rate && form.shift_hours && (
                <p className="text-[10px] tabular-nums" style={{ color: 'var(--warning)' }}>
                  {((parseFloat(form.overtime_rate_per_hour) / (parseFloat(form.daily_rate) / parseFloat(form.shift_hours))) * 100).toFixed(0)}% of regular rate
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Shift Hours</Label>
              <Input data-testid="worker-shift-input" type="number" value={form.shift_hours}
                onChange={e => setForm(p => ({...p, shift_hours: e.target.value}))}
                disabled={!editing} className="h-10 rounded-lg text-sm" />
            </div>
            <div className="flex items-end gap-4 pb-1">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({...p, is_active: v}))} disabled={!editing} />
                <Label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_temporary} onCheckedChange={v => setForm(p => ({...p, is_temporary: v}))} disabled={!editing} />
                <Label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Temporary</Label>
              </div>
            </div>
          </div>

          {/* Temporary dates */}
          {form.is_temporary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(217,119,6,0.04)', border: '1px solid rgba(217,119,6,0.15)' }}>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium" style={{ color: 'var(--warning)' }}>Start Date</Label>
                <Input type="date" value={form.temp_start_date} onChange={e => setForm(p => ({...p, temp_start_date: e.target.value}))}
                  disabled={!editing} className="h-10 rounded-lg text-sm" data-testid="worker-temp-start" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium" style={{ color: 'var(--warning)' }}>End Date</Label>
                <Input type="date" value={form.temp_end_date} onChange={e => setForm(p => ({...p, temp_end_date: e.target.value}))}
                  disabled={!editing} className="h-10 rounded-lg text-sm" data-testid="worker-temp-end" />
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))}
              disabled={!editing} className="h-20 rounded-lg text-sm resize-none" placeholder="Additional notes..." />
          </div>

          {editing && (
            <div className="flex justify-end pt-2">
              <Button data-testid="save-worker-button" onClick={handleSave} disabled={saving}
                className="h-10 rounded-xl px-6 text-sm font-medium" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : isNew ? 'Create Worker' : 'Save Changes'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-Garment Pricing (Outsourced Vendors) */}
      {!isNew && form.role === 'outsourced_vendor' && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Per Garment Pricing</CardTitle>
                <p className="text-[11px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                  Set different charge rates per garment type for this vendor
                </p>
              </div>
              <IndianRupee className="w-5 h-5" style={{ color: 'var(--brand-gold)', opacity: 0.6 }} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="piece-rate-table">
                <thead>
                  <tr className="border-b border-border/60">
                    <th className="text-left py-2 pr-4 text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Garment Type</th>
                    <th className="text-right py-2 pl-3 text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Rate per Piece (INR)</th>
                  </tr>
                </thead>
                <tbody>
                  {GARMENT_TYPES.filter(g => !['pattern_draft', 'pattern_cut'].includes(g)).map(garmentType => {
                    const rate = form.piece_rate_per_garment[garmentType];
                    return (
                      <tr key={garmentType} className="border-b border-border/30">
                        <td className="py-2.5 pr-4 font-medium" style={{ color: 'var(--navy-ink)' }}>
                          {GARMENT_LABELS[garmentType]}
                        </td>
                        <td className="py-2 pl-3 text-right">
                          {editing ? (
                            <div className="flex items-center justify-end gap-1.5">
                              <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{'\u20b9'}</span>
                              <Input
                                data-testid={`piece-rate-${garmentType}`}
                                type="number"
                                value={rate != null ? String(rate) : ''}
                                onChange={e => {
                                  const val = e.target.value;
                                  setForm(p => {
                                    const updated = { ...p.piece_rate_per_garment };
                                    if (val && !isNaN(val)) {
                                      updated[garmentType] = parseFloat(val);
                                    } else {
                                      delete updated[garmentType];
                                    }
                                    return { ...p, piece_rate_per_garment: updated };
                                  });
                                }}
                                className="h-8 w-28 rounded-lg text-sm text-right"
                                placeholder="-"
                              />
                            </div>
                          ) : (
                            <span className="tabular-nums font-medium" style={{ color: rate ? 'var(--navy-ink)' : 'var(--ink-muted)' }}>
                              {rate ? `\u20b9${rate.toLocaleString('en-IN')}` : '\u2014'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!editing && Object.keys(form.piece_rate_per_garment).length > 0 && (
              <div className="mt-3 p-2.5 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                <p className="text-[10px] font-medium" style={{ color: 'var(--ink-muted)' }}>
                  {Object.keys(form.piece_rate_per_garment).length} garment type{Object.keys(form.piece_rate_per_garment).length !== 1 ? 's' : ''} priced
                  {' \u00b7 '} Range: {'\u20b9'}{Math.min(...Object.values(form.piece_rate_per_garment)).toLocaleString('en-IN')}
                  {' - \u20b9'}{Math.max(...Object.values(form.piece_rate_per_garment)).toLocaleString('en-IN')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Capabilities Table */}
      {!isNew && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Capabilities</CardTitle>
              {canEdit && (
                <Button variant="outline" size="sm" onClick={() => openCapDialog()} className="h-8 rounded-lg text-xs gap-1.5"
                  data-testid="add-capability-button">
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {worker?.capabilities?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60">
                      <th className="text-left py-2 pr-4 text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Garment Type</th>
                      <th className="text-right py-2 px-3 text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Output/Day</th>
                      <th className="text-right py-2 px-3 text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Hours/Unit</th>
                      {canEdit && <th className="text-right py-2 pl-3 text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {worker.capabilities.map(cap => (
                      <tr key={cap.id} className="border-b border-border/30" data-testid={`capability-row-${cap.garment_type}`}>
                        <td className="py-2.5 pr-4 font-medium" style={{ color: 'var(--navy-ink)' }}>{cap.garment_type_label}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums" style={{ color: 'var(--navy-ink)' }}>{cap.output_per_day}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums" style={{ color: 'var(--navy-ink)' }}>{cap.hours_per_unit} hrs</td>
                        {canEdit && (
                          <td className="py-2 pl-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]"
                                onClick={() => openCapDialog(cap)}>Edit</Button>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" style={{ color: 'var(--danger)' }}
                                onClick={() => deleteCap(cap.id)}>Remove</Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-center py-6" style={{ color: 'var(--ink-muted)' }}>
                No capabilities defined yet. {canEdit ? 'Add one to get started.' : ''}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Availability Calendar */}
      {!isNew && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Availability</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => {
                  if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); }
                  else setCalMonth(m => m - 1);
                }}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium min-w-[120px] text-center" style={{ color: 'var(--navy-ink)' }}>
                  {MONTH_NAMES[calMonth - 1]} {calYear}
                </span>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => {
                  if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); }
                  else setCalMonth(m => m + 1);
                }}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mb-4">
              {Object.entries(AVAIL_STATUS).map(([key, val]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: val.bg, border: `1px solid ${val.color}` }} />
                  <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{val.label}</span>
                </div>
              ))}
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-white border border-border/60" />
                <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Working</span>
              </div>
            </div>

            <AvailabilityCalendar
              year={calYear}
              month={calMonth}
              availability={availability}
              loading={loadingAvail}
              canEdit={canEdit}
              onSetDay={setDayAvailability}
              onClearDay={clearDayAvailability}
            />
          </CardContent>
        </Card>
      )}

      {/* Capability Dialog */}
      <Dialog open={capDialogOpen} onOpenChange={setCapDialogOpen}>
        <DialogContent className="rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>{editingCapId ? 'Edit' : 'Add'} Capability</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Garment Type</Label>
              <Select value={capForm.garment_type} onValueChange={v => setCapForm(p => ({...p, garment_type: v}))}>
                <SelectTrigger className="h-10 rounded-lg text-sm" data-testid="cap-garment-type-select">
                  <SelectValue placeholder="Select garment type" />
                </SelectTrigger>
                <SelectContent>
                  {GARMENT_TYPES.map(g => (
                    <SelectItem key={g} value={g}>{GARMENT_LABELS[g]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Output / Day</Label>
                <Input type="number" step="0.1" value={capForm.output_per_day}
                  onChange={e => setCapForm(p => ({...p, output_per_day: e.target.value}))}
                  className="h-10 rounded-lg text-sm" placeholder="e.g. 2" data-testid="cap-output-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Hours / Unit</Label>
                <Input type="number" step="0.1" value={capForm.hours_per_unit}
                  onChange={e => setCapForm(p => ({...p, hours_per_unit: e.target.value}))}
                  className="h-10 rounded-lg text-sm" placeholder="e.g. 5" data-testid="cap-hours-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCapDialogOpen(false)} className="h-9 rounded-lg text-sm">Cancel</Button>
            <Button onClick={saveCap} className="h-9 rounded-lg text-sm" data-testid="save-capability-button"
              style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
              {editingCapId ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================================================
   Availability Calendar Component
   ============================================================ */
function AvailabilityCalendar({ year, month, availability, loading, canEdit, onSetDay, onClearDay }) {
  const [activeMenu, setActiveMenu] = useState(null);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const availMap = {};
  availability.forEach(a => { availMap[a.date] = a; });

  const handleDayClick = (dayNum) => {
    if (!canEdit) return;
    const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`;
    if (activeMenu === dateStr) {
      setActiveMenu(null);
    } else {
      setActiveMenu(dateStr);
    }
  };

  const handleStatusSelect = (dateStr, status) => {
    const existing = availMap[dateStr];
    if (existing && existing.status === status) {
      onClearDay(dateStr);
    } else {
      onSetDay(dateStr, status);
    }
    setActiveMenu(null);
  };

  if (loading) return <Skeleton className="h-64 w-full rounded-lg" />;

  return (
    <div>
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map(d => (
          <div key={d} className="text-center text-[10px] font-medium py-1" style={{ color: 'var(--ink-muted)' }}>{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before month starts */}
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}

        {/* Day cells */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1;
          const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`;
          const avail = availMap[dateStr];
          const status = avail?.status;
          const statusConfig = status ? AVAIL_STATUS[status] : null;
          const isToday = new Date().toISOString().split('T')[0] === dateStr;
          const isMenuOpen = activeMenu === dateStr;
          const isSunday = new Date(year, month - 1, dayNum).getDay() === 0;

          return (
            <div key={dayNum} className="relative">
              <button
                onClick={() => handleDayClick(dayNum)}
                data-testid={`cal-day-${dayNum}`}
                className={`w-full aspect-square rounded-lg flex flex-col items-center justify-center text-xs font-medium
                  ${canEdit ? 'cursor-pointer hover:ring-1 hover:ring-[color:var(--brand-gold)]' : 'cursor-default'}
                  ${isToday ? 'ring-2 ring-[color:var(--brand-navy)]' : ''}
                  ${isSunday && !statusConfig ? 'bg-slate-50' : ''}`}
                style={{
                  backgroundColor: statusConfig ? statusConfig.bg : undefined,
                  color: statusConfig ? statusConfig.color : isSunday ? 'var(--ink-muted)' : 'var(--navy-ink)',
                  transition: 'background-color 150ms',
                }}
              >
                <span>{dayNum}</span>
                {status && <span className="text-[8px] leading-none mt-0.5">{AVAIL_STATUS[status]?.label?.slice(0,3)}</span>}
              </button>

              {/* Status menu */}
              {isMenuOpen && canEdit && (
                <div className="absolute z-20 top-full left-1/2 -translate-x-1/2 mt-1 bg-white border border-border/80 rounded-lg shadow-lg p-1 min-w-[100px]"
                  style={{ animation: 'fadeIn 100ms' }}>
                  {Object.entries(AVAIL_STATUS).map(([key, val]) => (
                    <button key={key}
                      className={`w-full text-left text-[11px] px-2 py-1.5 rounded-md hover:bg-slate-50 flex items-center gap-2
                        ${status === key ? 'font-semibold' : ''}`}
                      style={{ color: val.color }}
                      onClick={() => handleStatusSelect(dateStr, key)}
                      data-testid={`avail-status-${key}`}
                    >
                      <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: val.bg, border: `1px solid ${val.color}` }} />
                      {val.label} {status === key ? '(clear)' : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Click outside to close menu */}
      {activeMenu && (
        <div className="fixed inset-0 z-10" onClick={() => setActiveMenu(null)} />
      )}
    </div>
  );
}
